import { Stack, Typography } from "@mui/material";
import PropTypes from "prop-types";
import { Fragment } from "react";
import {
  ContainedButtonWithForwardArrow,
  PrimaryDialog,
  StyledButton,
  WarningCustomIcon,
} from "react-common-ui-library";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import {
  ACCOUNT_ALREADY_EXISTS_MODAL,
  ACCOUNT_ALREADY_EXISTS_MODAL_CONTENT_1,
  ACCOUNT_ALREADY_EXISTS_MODAL_CONTENT_2,
  ACCOUNT_ALREADY_EXISTS_MODAL_CONTENT_3,
  ACCOUNT_ALREADY_EXISTS_MODAL_TITLE,
  THIS_USER,
  YOU,
} from "../../../../Constants/AccountAlreadyExistsModalConstants";
import {
  ACCOUNT_CREATION_ROUTE,
  ADMIN_PROVIDER_VIEW,
  CANCEL,
  CANCEL_BUTTON,
  CONSUMER_VIEW,
  CONTACT_NUMBER,
  LOGIN_ROUTE,
  LOG_IN,
  LOG_IN_BUTTON,
  WARNING_ICON_ID,
} from "../../../../Constants/CommonConstants";
import ConfigService from "../../../../Services/ConfigService";

/**
 * @description Account Already Exists Modal
 * @param {string} userView Consumer when we are dealing with consumer screen and adminProvider when dealing with Admin or Provider screens.
 * @param {boolean} open Boolean flag to show the modal/dialog
 * @param {function} setOpen Setter method to update the boolean flag
 * @returns {ReactNode} Renders the dialog to the user.
 */
const AccountAlreadyExistsModal = ({ userView, open, setOpen }) => {
  const intl = useIntl();

  const navigate = useNavigate();

  const userTypeText = userView === CONSUMER_VIEW ? YOU : THIS_USER;

  const onCancelButtonClickHandler = () => {
    setOpen(false);
    navigate(`/${ACCOUNT_CREATION_ROUTE}`);
  };

  const navigateToLogIn = () => {
    setOpen(false);
    navigate(`/${LOGIN_ROUTE}`);
  };

  const contactNumber = ConfigService(CONTACT_NUMBER);

  return (
    <PrimaryDialog
      id={ACCOUNT_ALREADY_EXISTS_MODAL}
      open={open}
      title={intl.formatMessage({ id: ACCOUNT_ALREADY_EXISTS_MODAL_TITLE })}
      titleIcon={<WarningCustomIcon id={WARNING_ICON_ID} />}
      actions={
        <Fragment>
          <ContainedButtonWithForwardArrow
            id={LOG_IN_BUTTON}
            onClick={navigateToLogIn}
          >
            {intl.formatMessage({ id: LOG_IN })}
          </ContainedButtonWithForwardArrow>
          <StyledButton id={CANCEL_BUTTON} onClick={onCancelButtonClickHandler}>
            {intl.formatMessage({ id: CANCEL })}
          </StyledButton>
        </Fragment>
      }
    >
      <Stack spacing={2}>
        <Typography id={ACCOUNT_ALREADY_EXISTS_MODAL_CONTENT_1}>
          {intl.formatMessage(
            { id: ACCOUNT_ALREADY_EXISTS_MODAL_CONTENT_1 },
            { userTypeText: intl.formatMessage({ id: userTypeText }) }
          )}
        </Typography>
        <Typography id={ACCOUNT_ALREADY_EXISTS_MODAL_CONTENT_2}>
          {intl.formatMessage(
            { id: ACCOUNT_ALREADY_EXISTS_MODAL_CONTENT_2 },
            { contactNumber: contactNumber }
          )}
        </Typography>
        {userView === ADMIN_PROVIDER_VIEW ? (
          <Typography id={ACCOUNT_ALREADY_EXISTS_MODAL_CONTENT_3}>
            {intl.formatMessage({ id: ACCOUNT_ALREADY_EXISTS_MODAL_CONTENT_3 })}
          </Typography>
        ) : null}
      </Stack>
    </PrimaryDialog>
  );
};

export default AccountAlreadyExistsModal;

AccountAlreadyExistsModal.propTypes = {
  /**
   * consumer when we are dealing with consumer screen and adminProvider when dealing with Admin or Provider screens.
   */
  userView: PropTypes.string.isRequired,
  /**
   * Boolean flag to show the modal/dialog
   */
  open: PropTypes.bool.isRequired,
  /**
   * Setter method to update the boolean flag
   */
  setOpen: PropTypes.func.isRequired,
};
